import { Divider } from '@mui/material';
import type { LegacyPaymentOption, PaymentOption, RoomRate } from '@niarab2c/frontend-commons/src/types/hotels';
import { ShoppingCart, useDispatch, useSelector, type CriteriaForm } from '@niarab2c/niarab2c-core/src/app/base';
import useQueryStringHotelCriteria from '@niarab2c/niarab2c-core/src/hooks/useQueryStringHotelCriteria';
import useStartDateValues from '@niarab2c/niarab2c-core/src/hooks/useStartDateValues';
import { Tooltip } from '@niaratech/niara-react-components';
import _groupBy from 'lodash/groupBy';
import _pick from 'lodash/pick';
import * as React from 'react';
import { ShoppingCartState } from '@niarab2c/niarab2c-core/src/app/reducers/shoppingCart';
import { IcNoImageCart } from '../../Icons';
import DebugButton from '../../microComponents/DebugButton';
import CartRoom, { CartRoomProps } from './CartRoom/CartRoom';
import HotelCartTitle from './CartTitle';
import HotelCartTotal, { CartTotalProps } from './CartTotal';
import * as S from './styles';
type CartItem = CartRoomProps & {
  criteria: CriteriaForm;
};
export type HotelCartProps = {
  cartItems: CartItem[];
  cartTotal: CartTotalProps['cartTotal'];
  paymentOptions?: LegacyPaymentOption[] | PaymentOption[];
  onCheckout: CartTotalProps['onCheckout'];
  onGoToCart?: CartTotalProps['onGoToCart'];
  onCreateQuotation?: CartTotalProps['onCreateQuotation'];
  showComissionAndMarkup?: 'tenant' | 'client' | false | null;
  expectedRoomsCount?: number;
  isForGroups?: boolean;
  numberOfItems?: number;
  addonCartItems?: Array<ShoppingCartState['addonCartItems'][number] & {
    onRemove?: () => void;
  }>;
};
const HotelCart: React.FC<HotelCartProps> = ({
  cartItems,
  cartTotal,
  paymentOptions,
  onCheckout,
  onCreateQuotation,
  showComissionAndMarkup,
  onGoToCart,
  expectedRoomsCount,
  isForGroups,
  numberOfItems,
  addonCartItems
}) => {
  const {
    defaultStartDate,
    defaultEndDate
  } = useStartDateValues();
  const [queryCriteria] = useQueryStringHotelCriteria({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    rooms: [{
      adults: 1
    }]
  });
  const {
    hotelResult,
    user
  } = useSelector(s => ({
    hotelResult: s.hotel.hotelDetails?.hotelResult,
    user: s.authentication?.user
  }));
  const dispatch = useDispatch();
  const groupedCartItemsByCriteria = React.useMemo(() => Object.values(_groupBy(cartItems, cartItem => {
    return JSON.stringify(cartItem?.criteria);
  })).sort((a, b) => new Date(a[0]?.criteria?.startDate).getTime() - new Date(b[0]?.criteria?.startDate).getTime()), [cartItems]);
  const groupedCartItemsByHotel = React.useCallback((cartItems: CartItem[]) => {
    const groupName = _groupBy(cartItems, cartItem => JSON.stringify(_pick(cartItem, 'hotelId', 'startDate', 'endDate')));
    return Object.values(groupName).sort((a, b) => new Date(a[0]?.criteria?.startDate).getTime() - new Date(b[0]?.criteria?.startDate).getTime());
  }, []);
  const numberOfPendingItems = expectedRoomsCount - numberOfItems;
  const selectRoomQuantity = React.useCallback((roomRates: [RoomRate], quantity: number) => {
    dispatch(ShoppingCart.setHotelRoomRatesQuantity({
      roomRates,
      hotelResult,
      quantity,
      isForGroups,
      buyer: user,
      criteria: queryCriteria
    }));
  }, [dispatch, hotelResult, isForGroups, queryCriteria, user]);
  return <>
      <S.CartCard>
        <DebugButton value={cartItems} />
        <S.CardContent>
          {groupedCartItemsByCriteria?.length > 0 ? groupedCartItemsByCriteria?.map((cartItemGroup, idx) => {
          const groupedByHotel = groupedCartItemsByHotel(cartItemGroup);
          return <React.Fragment key={idx}>
                  <HotelCartTitle criteria={cartItemGroup?.[0]?.criteria} />
                  <S.ContentCart>
                    <S.GroupCartItem>
                      <S.IconRoom src={cartItemGroup?.[0]?.hotelImage ?? IcNoImageCart} alt="" />

                      <div>
                        <Tooltip title={cartItemGroup[0].hotelName}>
                          <S.HotelTitle>{cartItemGroup[0].hotelName}</S.HotelTitle>
                        </Tooltip>

                        {cartItemGroup[0].cityName && <S.TextCity>{cartItemGroup[0].cityName}</S.TextCity>}
                      </div>
                    </S.GroupCartItem>
                    {groupedByHotel?.map((group, idx) => {
                return <React.Fragment key={idx}>
                          {group.map((cartItem, index) => {
                    return <S.ContainerItems key={index}>
                                <Divider sx={{
                        marginBlock: '9px'
                      }} />
                                <CartRoom {...cartItem} showOnlyTotal disableTax quantity={cartItem.quantity} policyDescription={cartItem?.roomRates?.[0]?.descriptions?.cancelPolicy} showComissionAndMarkup={showComissionAndMarkup} isForGroups={isForGroups} maxQuantity={cartItem?.roomRates?.[0]?.roomType?.units} setQuantity={quantity => selectRoomQuantity(cartItem?.roomRates, quantity)} addonCartItems={addonCartItems} />
                              </S.ContainerItems>;
                  })}
                        </React.Fragment>;
              })}
                  </S.ContentCart>
                </React.Fragment>;
        }) : <>
              <S.Divider />
              <S.EmptyCartMessageContainer>
                <p>Selecione quartos da lista ao lado para reservar.</p>
              </S.EmptyCartMessageContainer>
            </>}
        </S.CardContent>
        {isForGroups && numberOfPendingItems > 0 && <S.AlertRoomsCount>
            <p>{`Falta selecionar ${numberOfPendingItems} de ${expectedRoomsCount} quartos`}</p>
          </S.AlertRoomsCount>}
        {cartItems?.length > 0 && <S.CartTotalContainer>
            <HotelCartTotal cartTotal={cartTotal} paymentOptions={paymentOptions} onCheckout={onCheckout} onCreateQuotation={onCreateQuotation} onGoToCart={onGoToCart} />
          </S.CartTotalContainer>}
      </S.CartCard>
    </>;
};
export default HotelCart;